import React, { useEffect, useRef, useState } from "react";
import { Send } from "lucide-react";
import {
  AppButton, Callout, FilePreview, FileUpload, FormField, ProgressBar, Textarea,
} from "../../ui";
import { ACCEPT, ACCEPTED_HINT, MAX_FILE_MB, MAX_FILES, fileProblem } from "./assignmentFiles";
import { LIMITS, formatDue } from "./assignmentRules";
import useUploadRequest, { uploadFailure } from "./useUploadRequest";

const draftKey = (assignmentId) => `ga-draft:${assignmentId}`;

/** The unsent answer, kept in this browser only — a convenience, never the record. */
const readDraft = (assignmentId) => {
  try {
    const raw = window.localStorage.getItem(draftKey(assignmentId));
    const draft = raw ? JSON.parse(raw) : null;
    return draft && typeof draft.text === "string" ? draft : null;
  } catch {
    return null;
  }
};
const writeDraft = (assignmentId, text) => {
  try {
    if (text) window.localStorage.setItem(draftKey(assignmentId), JSON.stringify({ text, savedAt: Date.now() }));
    else window.localStorage.removeItem(draftKey(assignmentId));
  } catch {
    // Private window or storage blocked: the form still works, it just won't remember.
  }
};

const SUBMIT_LABEL = { new: "Hand in", edit: "Save changes", resubmit: "Resubmit" };

/**
 * StudentSubmissionForm — hand in, change or resubmit work on a batch assignment.
 *
 * What may happen is the server's answer (`submissionState`); this form only
 * collects the work. Robustness is the point:
 *   · the answer is kept as a draft in this browser while it is typed, so a
 *     refresh, a crash or a dropped connection does not lose it
 *   · files are checked before they are sent, and a file the server still
 *     refuses comes back by name with everything else kept
 *   · progress is shown and the upload can be cancelled; leaving mid-upload asks
 *   · on a change or a resubmission, files already handed in can be kept
 *
 * Props:
 *   assignmentId, submission (null for a first hand-in), submissionState
 *   onSubmitted(data) — the server's { submission, submissionState }
 *   onStale()         — the server says things changed; reload the page's data
 *   onCancel          — offered when changing work already handed in
 */
const StudentSubmissionForm = ({ assignmentId, submission, submissionState, onSubmitted, onStale, onCancel }) => {
  const mode = submissionState?.mode || "new";
  const serverText = mode === "new" ? "" : submission?.textResponse || "";
  const upload = useUploadRequest();

  const [restored, setRestored] = useState(() => {
    const draft = readDraft(assignmentId);
    return draft && draft.text !== serverText ? draft : null;
  });
  const [text, setText] = useState(restored ? restored.text : serverText);
  const [kept, setKept] = useState(mode === "new" ? [] : submission?.attachments || []);
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const textRef = useRef(null);

  // Remember the answer as it is typed, a beat after the last keystroke.
  useEffect(() => {
    const t = setTimeout(() => writeDraft(assignmentId, text === serverText ? "" : text), 500);
    return () => clearTimeout(t);
  }, [assignmentId, text, serverText]);

  const slots = MAX_FILES - kept.length;
  const busy = upload.sending;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setFieldError("");
    const answer = text.trim();
    if (!answer && !kept.length && !files.length) {
      setFieldError("Write an answer or attach a file before handing in.");
      textRef.current?.focus();
      return;
    }
    if (answer.length > LIMITS.textResponse) {
      setFieldError(`Keep your answer under ${LIMITS.textResponse.toLocaleString("en-IN")} characters — attach a file for anything longer.`);
      textRef.current?.focus();
      return;
    }

    const fd = new FormData();
    fd.append("textResponse", answer);
    fd.append("keepAttachments", JSON.stringify(kept.map((a) => a.fileKey)));
    files.forEach((f) => fd.append("attachments", f));

    try {
      const data = await upload.send("post", `/general-assignments/${assignmentId}/submit`, fd);
      writeDraft(assignmentId, "");
      onSubmitted(data);
    } catch (err) {
      const message = uploadFailure(err, "Your work could not be handed in. Nothing was lost — try again.");
      if (message === null) return;
      const code = err?.response?.data?.code;
      setError(message);
      if (["SUBMISSION_CLOSED", "SUBMISSION_CHANGED", "ALREADY_SUBMITTED"].includes(code)) onStale?.();
    }
  };

  return (
    <form onSubmit={submit} noValidate className="tw-flex tw-flex-col tw-gap-4">
      {error && <Callout tone="danger" role="alert">{error}</Callout>}

      {submissionState?.willBeLate && (
        <Callout tone="warning">
          The due date has passed. You can still hand in, but your work will be marked late.
        </Callout>
      )}
      {mode === "edit" && submissionState?.deadline && !submissionState.willBeLate && (
        <Callout tone="info">
          You can change your work until {formatDue(submissionState.deadline, { dateOnly: submissionState.dateOnly })}, or until it is graded.
        </Callout>
      )}

      {restored && (
        <Callout
          tone="info"
          action={(
            <AppButton
              type="button"
              size="xs"
              variant="outline"
              onClick={() => {
                setText(serverText);
                setRestored(null);
                writeDraft(assignmentId, "");
              }}
            >
              Discard draft
            </AppButton>
          )}
        >
          We kept the answer you were writing on this device{restored.savedAt ? ` (${formatDue(restored.savedAt)})` : ""}.
        </Callout>
      )}

      <FormField
        label="Your answer"
        error={fieldError}
        hint={`Write here, paste a link (GitHub, Drive…), or attach files below. ${text.length.toLocaleString("en-IN")} / ${LIMITS.textResponse.toLocaleString("en-IN")}`}
      >
        <Textarea
          ref={textRef}
          rows={7}
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (fieldError) setFieldError("");
          }}
          maxLength={LIMITS.textResponse}
          placeholder="Type your answer…"
          disabled={busy}
        />
      </FormField>

      <div className="tw-flex tw-flex-col tw-gap-2">
        {kept.length > 0 && (
          <>
            <p className="tw-m-0 tw-text-[0.8125rem] tw-font-medium tw-text-content">Files you handed in before</p>
            <FilePreview
              attachments={kept}
              compact
              onRemove={busy ? undefined : (key) => setKept((list) => list.filter((a) => a.fileKey !== key))}
            />
          </>
        )}
        {slots > 0 ? (
          <FileUpload
            label={kept.length ? "Add files" : "Attach files (optional)"}
            files={files}
            onChange={(next) => {
              setFiles(next);
              if (fieldError) setFieldError("");
            }}
            accept={ACCEPT}
            maxFiles={slots}
            maxSizeMB={MAX_FILE_MB}
            validate={fileProblem}
            hint="Drop your files here, or click to choose"
            typesHint={ACCEPTED_HINT}
            disabled={busy}
          />
        ) : (
          <p className="tw-m-0 tw-text-[0.8125rem] tw-text-content-muted">
            {MAX_FILES} files is the most you can hand in. Remove one to add another.
          </p>
        )}
      </div>

      {busy && files.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-1" role="status" aria-live="polite">
          <span className="tw-text-[0.8125rem] tw-text-content-muted">Uploading your files… {upload.progress}%</span>
          <ProgressBar value={upload.progress} color="primary" />
        </div>
      )}

      <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <AppButton type="submit" disabled={busy}>
          <Send size={16} /> {busy ? "Handing in…" : SUBMIT_LABEL[mode] || SUBMIT_LABEL.new}
        </AppButton>
        {busy ? (
          <AppButton type="button" variant="outline" onClick={upload.cancel}>Cancel</AppButton>
        ) : (
          onCancel && <AppButton type="button" variant="ghost" onClick={onCancel}>Keep what I handed in</AppButton>
        )}
      </div>
    </form>
  );
};

export default StudentSubmissionForm;
